import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";

// Opus Agent drawer persistence, AGENT SURFACE ONLY: whether the drawer was
// open and which chat it showed, restored across reloads. The shared
// chatSidebarStore stays in-memory on purpose — the trainee KB drawer keeps
// closing on reload, and the TopNav button / Page layout keep reading it;
// OpusAgentChat hydrates it from here on mount and writes through.
//
// `isOpen` is a device preference. `selectedChatId` belongs to the signed-in
// user: it is tagged with `userId`, ignored and cleared when another user
// reads it (shared device), and cleared in logout.tsx.
type DrawerState = {
  isOpen: boolean;
  selectedChatId: string | null;
  userId: string | null;
  setOpen: (isOpen: boolean) => void;
  setSelectedChat: (chatId: string | null, userId: string) => void;
  clearSelectedChat: () => void;
};

const sanitizeId = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

export const useOpusAgentDrawerStore = create<DrawerState>()(
  persist(
    (set) => ({
      isOpen: false,
      selectedChatId: null,
      userId: null,
      setOpen: (isOpen) => set({ isOpen }),
      setSelectedChat: (chatId, userId) =>
        set({ selectedChatId: chatId, userId }),
      clearSelectedChat: () => set({ selectedChatId: null, userId: null }),
    }),
    {
      name: "opus-agent-drawer",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({
        isOpen: state.isOpen,
        selectedChatId: state.selectedChatId,
        userId: state.userId,
      }),
      merge: (persisted, current) => {
        const stored = persisted as {
          isOpen?: unknown;
          selectedChatId?: unknown;
          userId?: unknown;
        } | null;
        // A selection is only meaningful with the user it belongs to.
        const userId = sanitizeId(stored?.userId);
        return {
          ...current,
          isOpen: stored?.isOpen === true,
          selectedChatId: userId ? sanitizeId(stored?.selectedChatId) : null,
          userId,
        };
      },
    },
  ),
);

// The persisted selection for THIS user. An entry left by another user on a
// shared device is ignored and cleared. null = nothing to restore (also for
// a persisted new composition).
export const readPersistedSelectedChat = (userId: string): string | null => {
  const state = useOpusAgentDrawerStore.getState();
  if (state.userId === null) return null;
  if (state.userId !== userId) {
    state.clearSelectedChat();
    return null;
  }
  return state.selectedChatId;
};

// Signed-in-user data: cleared on logout (the open state is a device
// preference and stays).
export const clearOpusAgentDrawerSelection = (): void =>
  useOpusAgentDrawerStore.getState().clearSelectedChat();
